import { defineStore } from "pinia"

export const useCargoStore = defineStore("cargo", {
  state: () => ({
    cargoTypes: [],
    cargoCategories: [],
    cargoRequests: [],
    isLoading: false,
    error: null,
  }),

  getters: {
    activeCargoTypes: (state) => state.cargoTypes.filter((type) => type.is_active),
    getCargoTypeById: (state) => (id) => state.cargoTypes.find((type) => type.id === id),
    getCargoRequestById: (state) => (id) => state.cargoRequests.find((request) => request.id === id),
  },

  actions: {
    async fetchCargoTypes(activeOnly = false) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")

        const response = await this.$axios.get("/api/v1/shipment/cargo-types", {
          params: { active_only: activeOnly },
        })

        this.cargoTypes = response.data.data
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch cargo types"
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async createCargoType(cargoTypeData) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")

        const response = await this.$axios.post("/api/v1/shipment/cargo-types", cargoTypeData)
        this.cargoTypes.unshift(response.data.data)
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create cargo type"
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async updateCargoType(id, cargoTypeData) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")

        const response = await this.$axios.put(`/api/v1/shipment/cargo-types/${id}`, cargoTypeData)
        const index = this.cargoTypes.findIndex((type) => type.id === id)
        if (index !== -1) {
          this.cargoTypes[index] = response.data.data
        }
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update cargo type"
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async deleteCargoType(id) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")

        const response = await this.$axios.delete(`/api/v1/shipment/cargo-types/${id}`)
        this.cargoTypes = this.cargoTypes.filter((type) => type.id !== id)
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to delete cargo type"
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async toggleCargoTypeStatus(id) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")

        const response = await this.$axios.patch(`/api/v1/shipment/cargo-types/${id}/toggle-status`)
        const index = this.cargoTypes.findIndex((type) => type.id === id)
        if (index !== -1) {
          this.cargoTypes[index] = response.data.data
        }
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to toggle cargo type status"
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async fetchCargoCategories(activeOnly = false) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")

        const response = await this.$axios.get("/api/v1/shipment/cargo-categories", {
          params: { active_only: activeOnly },
        })
        this.cargoCategories = response.data.data
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch cargo categories"
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async createCargoCategory(cargoCategoryData) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")

        const response = await this.$axios.post("/api/v1/shipment/cargo-categories", cargoCategoryData)
        this.cargoCategories.unshift(response.data.data)
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create cargo category"
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async updateCargoCategory(id, cargoCategoryData) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")

        const response = await this.$axios.put(`/api/v1/shipment/cargo-categories/${id}`, cargoCategoryData)
        const index = this.cargoCategories.findIndex((category) => category.id === id)
        if (index !== -1) {
          this.cargoCategories[index] = response.data.data
        }
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update cargo category"
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async deleteCargoCategory(id) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")

        const response = await this.$axios.delete(`/api/v1/shipment/cargo-categories/${id}`)
        this.cargoCategories = this.cargoCategories.filter((category) => category.id !== id)
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to delete cargo category"
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async toggleCargoCategoryStatus(id) {
      this.isLoading = true
      this.error = null
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")

        const response = await this.$axios.patch(`/api/v1/shipment/cargo-categories/${id}/toggle-status`)
        const index = this.cargoCategories.findIndex((category) => category.id === id)
        if (index !== -1) {
          this.cargoCategories[index] = response.data.data
        }
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to toggle cargo category status"
        throw error
      } finally {
        this.isLoading = false
      }
    },

    // Cargo Request Actions
    async createCargoRequest(requestData) {
      // Using this.$axios injected by pinia plugin
      try {
        await this.$axios.get("/sanctum/csrf-cookie")
        const response = await this.$axios.post("/api/v1/shipment/cargo-requests", requestData)
        this.cargoRequests.unshift(response.data.data)
        return response.data.data
      } catch (error) {
        throw error
      }
    },

    async fetchCargoRequests(filters = {}) {
      const { $toast } = useNuxtApp()
      this.isLoading = true
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.get("/sanctum/csrf-cookie")
        const response = await this.$axios.get("/api/v1/shipment/cargo-requests", { params: filters })
        this.cargoRequests = response.data.data
        return response.data
      } catch (error) {
        $toast.error(error.response?.data?.message || "Failed to fetch cargo requests")
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async updateCargoRequest(id, requestData) {
      const { $toast } = useNuxtApp()
      return $toast.promise(
        async () => {
          // Using this.$axios injected by pinia plugin
          await this.$axios.get("/sanctum/csrf-cookie")
          const response = await this.$axios.put(`/api/v1/shipment/cargo-requests/${id}`, requestData)
          const index = this.cargoRequests.findIndex((request) => request.id === id)
          if (index !== -1) {
            this.cargoRequests[index] = response.data.data
          }
          return response.data
        },
        {
          loading: "Updating cargo request...",
          success: "Cargo request updated successfully!",
          error: (err) => err.response?.data?.message || "Failed to update cargo request",
          duration: 3000,
          position: "top-right",
        }
      )
    },

    async cancelCargoRequest(id, reason = "") {
      const { $toast } = useNuxtApp()
      return $toast.promise(
        async () => {
          // Using this.$axios injected by pinia plugin
          await this.$axios.get("/sanctum/csrf-cookie")
          const response = await this.$axios.patch(`/api/v1/shipment/cargo-requests/${id}/cancel`, { reason })
          const index = this.cargoRequests.findIndex((request) => request.id === id)
          if (index !== -1) {
            this.cargoRequests[index] = response.data.data
          }
          return response.data
        },
        {
          loading: "Cancelling cargo request...",
          success: "Cargo request cancelled successfully!",
          error: (err) => err.response?.data?.message || "Failed to cancel cargo request",
          duration: 3000,
          position: "top-right",
        }
      )
    },
  }
})
